/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import {
    useTheme,
    lighten,
    darken,
    Typography,
    Stack,
    Tooltip,
    IconButton,
    TextField,
    Divider,
    CircularProgress,
} from "@mui/material";
import { ChecklistItem, Effect, Setter } from "../../utils/Interfaces";
import { Edit, Check, Close, Delete } from "@mui/icons-material";
import {
    deleteTechnicianChecklistItem,
    renameTechnicianChecklistItem,
} from "../../features/office-work-order/technician-entry/technicianEntryFunctions";

import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { technicianTaskIcon } from "../general/IconSwitch";

export interface TechnicianChecklistItemProps {
    checklistItem: ChecklistItem;
    editable: boolean;
    onChange: Setter<Effect> | Effect;
    contrastBg: boolean;
}

const TechnicianChecklistItem = (props: TechnicianChecklistItemProps) => {
    const { checklistItem, contrastBg, editable, onChange } = props;

    const [editingLabel, setEditingLabel] = useState<boolean>(false);
    const [loadingLabelChange, setLoadingLabelChange] = useState<boolean>(false);
    const [label, setLabel] = useState<string>(checklistItem.label);

    const theme = useTheme();

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    useEffect(() => {}, [checklistItem, editable]);

    let background = lighten(theme.palette.background.paper, 0.07);

    if (contrastBg) {
        background = darken(background, 0.05);
    }

    // let keyId: string = checklistItem.label.toLowerCase().replaceAll(/\W/g, "");

    return (
        <Stack direction="row" justifyContent="space-between" style={{ background: background }} sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="flex-start">
                {!editingLabel && (
                    <Stack direction="column" justifyContent="center">
                        <Typography>{checklistItem.label}</Typography>
                    </Stack>
                )}
                {editingLabel && (
                    <Stack direction="column" justifyContent="center">
                        <TextField
                            disabled={loadingLabelChange}
                            variant="outlined"
                            size="small"
                            label="Rename Checklist Description"
                            value={label}
                            onChange={(event) => setLabel(event.target.value)}
                            sx={{ width: 400 }}
                        />
                    </Stack>
                )}
                {editable && (
                    <Stack direction="row" justifyContent="flex-start">
                        {!editingLabel && !loadingLabelChange && (
                            <Stack direction="column" justifyContent="center">
                                <Tooltip title="Edit task description">
                                    <IconButton sx={{ ml: 1 }} onClick={() => setEditingLabel(true)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        )}

                        {loadingLabelChange && (
                            <Stack direction="column" justifyContent="center">
                                <CircularProgress size={20} sx={{ ml: 2 }} />
                            </Stack>
                        )}

                        {editingLabel && !loadingLabelChange && (
                            <Stack direction="row" justifyContent="flex-start">
                                <Stack direction="column" justifyContent="center">
                                    <Tooltip title="Cancel changes">
                                        <IconButton
                                            sx={{ ml: 1 }}
                                            onClick={() => {
                                                setEditingLabel(false);
                                                setLabel(checklistItem.label);
                                            }}
                                        >
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                <Stack direction="column" justifyContent="center">
                                    <Tooltip title="Confirm changes">
                                        <IconButton
                                            onClick={() => {
                                                setLoadingLabelChange(true);
                                                renameTechnicianChecklistItem(
                                                    checklistItem.id,
                                                    label,
                                                    dispatch,
                                                    () => {
                                                        onChange(() => {
                                                            setLoadingLabelChange(false);
                                                            setEditingLabel(false);
                                                        });
                                                    },
                                                    (loading: boolean) => {}
                                                );
                                            }}
                                        >
                                            <Check fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        )}
                        {!loadingLabelChange && (
                            <>
                                <Divider orientation="vertical" sx={{ ml: 1, mr: 1 }} />
                                <Stack direction="column" justifyContent="center">
                                    <Tooltip title="Delete task">
                                        <IconButton
                                            onClick={() => {
                                                setEditingLabel(false);
                                                setLoadingLabelChange(true);
                                                deleteTechnicianChecklistItem(
                                                    checklistItem.id,
                                                    dispatch,
                                                    () =>
                                                        onChange(() => {
                                                            setLoadingLabelChange(false);
                                                        }),
                                                    (loading: boolean) => {}
                                                );
                                            }}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </>
                        )}
                    </Stack>
                )}
            </Stack>
            <Stack direction="row" justifyContent="space-between">
                {checklistItem.checked_by && (
                    <Stack direction="column" justifyContent="flex-end" sx={{ mr: 3 }}>
                        <Typography variant="caption" textAlign="right">
                            {new Date(checklistItem.completed_timestamp).toLocaleDateString()}&nbsp;
                            {new Date(checklistItem.completed_timestamp).toLocaleTimeString()}
                        </Typography>
                        <Typography
                            variant="caption"
                            textAlign="right"
                        >{`${checklistItem.checked_by.first_name} ${checklistItem.checked_by.last_name}`}</Typography>
                    </Stack>
                )}
                <Stack direction="column" justifyContent="center">
                    {technicianTaskIcon(checklistItem.status)}
                </Stack>
            </Stack>
        </Stack>
    );
};

export default TechnicianChecklistItem;
